import * as React from 'react';
import styled, { css } from 'styled-components/macro';
import { LoadingIndicator } from 'lib/components';

import * as theme from 'lib/style/theme';
import { useEffect, useState } from 'react';

type RowConfig = {
  key: string | number;
  columns: React.ReactNode[];
  onClick?: () => void;
};

type Props = {
  headers?: React.ReactNode[];
  rows?: RowConfig[];
  columnWidths?: any[];
  hoverable?: boolean;
  isLoading?: boolean;
  loaderHeight?: number;
  error?: boolean | null;
  compact?: boolean;
  relative?: boolean;
  fixColumnIndex?: number[];
  fixColumnIndicator?: boolean;
  cellHeight?: string;
  style?: React.CSSProperties;
  dataCy?: string;
  borderColor?: string;
  thFontSize?: string;
  tdFontSize?: string;
  thFontColor?: string;
  tdFontWeight?: number;
  horizontalScroll?: boolean;
};

const TableWrapper = styled.div`
  width: 100%;
  position: relative;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  overflow: overlay;
`;

type ThProps = {
  compact: boolean;
  fix: boolean | string;
  index: number;
  isScrolledHorizontally?: boolean;
};

type TbodyTrProps = { hoverable: boolean; compact: boolean };

type TdProps = {
  index: number;
  compact: boolean;
  fix?: boolean | string;
  cellHeight: string;
  isScrolledHorizontally?: boolean;
};

export const Table = ({
  headers,
  compact = false,
  columnWidths = [],
  cellHeight = '',
  rows = [],
  hoverable = false,
  isLoading = false,
  error = false,
  relative = false,
  fixColumnIndex = [],
  fixColumnIndicator = false,
  style,
  dataCy,
  borderColor = '#dee2e8',
  thFontSize = theme.fontSizes.sm,
  tdFontSize = theme.fontSizes.md,
  thFontColor = theme.palette.blackRgb60,
  tdFontWeight = 500,
  loaderHeight = 300,
  horizontalScroll = false,
}: Props) => {
  const getFixedColumnLeftOffset = (index: number): string => {
    const offset = fixColumnIndex
      .slice(0, fixColumnIndex.indexOf(index))
      .reduce((total, idx) => total + (columnWidths[idx] || 150), 0);
    return `${offset}px`;
  };
  const getFixedColumnIndicator = (
    index: number,
    isScrolledHorizontally?: boolean
  ): string => {
    if (
      !isScrolledHorizontally ||
      !fixColumnIndicator ||
      !fixColumnIndex ||
      !fixColumnIndex.length ||
      fixColumnIndex.length === headers?.length
    ) {
      return 'none';
    }
    return index === Math.max(...fixColumnIndex)
      ? 'inset -3px 0 3px -3px rgba(0, 0, 0, 0.3)'
      : 'none';
  };
  const TbodyTr = styled.tr<TbodyTrProps>`
    ${props =>
      props.hoverable &&
      css`
        cursor: pointer;
        &:hover {
          background-color: ${theme.palette.white};
          box-shadow:
            0 2px 4px 0 rgba(52, 58, 64, 0.1),
            0 4px 24px 0 rgba(52, 58, 64, 0.15);
        }
      `}

    &:last-of-type > td {
      &:first-of-type,
      &:last-of-type {
        border-bottom: ${props =>
          props.compact ? `2px solid ${borderColor}!important` : '0'};
      }
      border-bottom: 2px solid ${borderColor};
    }
  `;

  const Th = styled.th<ThProps>`
    ${theme.fontNormal500};
    font-size: ${thFontSize};
    color: ${thFontColor};
    border-width: 1px 0 2px 0;
    padding: ${props => (props.compact ? '0' : '8px 8px')};
    border-bottom: 2px solid ${borderColor};
    text-align: start;

    &:nth-of-type(2) {
      text-align: start;
    }
    /*
  &:nth-of-type(6) {
    text-align: end;
  }
  */
    &:nth-last-of-type(2) {
      text-align: end;
    }

    &:first-of-type {
      border-bottom: ${props =>
        props.compact ? `2px solid ${borderColor}` : '0'};
      padding: ${props => (props.compact ? '0' : '0px 4px')};
    }

    &:last-of-type {
      border-bottom: ${props =>
        props.compact ? `2px solid ${borderColor}!important` : '0'};
      padding: ${props => (props.compact ? '0' : '0px 4px')};
    }
    ${props =>
      props.fix &&
      css`
        background-color: white;
        /* Stick to the left */
        left: ${getFixedColumnLeftOffset(props.index)};
        box-shadow: ${getFixedColumnIndicator(
          props.index,
          props.isScrolledHorizontally
        )};
        position: sticky;
        /* Displayed on top of other rows when scrolling */
        z-index: 4;
      `}
  `;

  const Td = styled.td<TdProps>`
    ${theme.fontNormal500}
    white-space: nowrap;
    font-size: ${tdFontSize};
    color: ${theme.palette.themeDark};
    font-weight: ${tdFontWeight};
    padding: ${props =>
      props.compact
        ? '0px'
        : Number(props.index) === 1
          ? '6px 8px'
          : '20px 8px'};
    height: ${props =>
      !props.cellHeight && props.compact ? '55px' : props.cellHeight || 'auto'};
    ${props =>
      props.index === 1 &&
      css`
        width: 150px;
      `}
    border-bottom: 1px solid ${borderColor};
    box-sizing: border-box;
    text-align: start;

    &:nth-of-type(2) {
      text-align: left;
    }

    &:nth-last-of-type(2) {
      text-align: end;
      position: relative;
    }

    &:first-of-type {
      border-bottom: ${props =>
        props.compact ? `1px solid ${borderColor}` : '0'};
      padding: ${props => (props.compact ? '0' : '0px 4px')};
    }

    &:last-of-type {
      border-bottom: ${props =>
        props.compact ? `1px solid ${borderColor}!important` : '0'};
      padding: ${props => (props.compact ? '0' : '0px 4px')};
    }
    ${props =>
      props.fix &&
      css`
        background-color: white;
        /* Stick to the left */
        left: ${getFixedColumnLeftOffset(props.index)};
        box-shadow: ${getFixedColumnIndicator(
          props.index,
          props.isScrolledHorizontally
        )};
        position: sticky !important;
        /* Displayed on top of other rows when scrolling */
        z-index: 4;
      `}
  `;

  const [isScrolledHorizontally, setIsScrolledHorizontally] = useState(false);
  const tableWrapperRef = React.useRef<HTMLDivElement>(null);

  // Add scroll event listener to show fix column indicator only when scrolled horizontally
  useEffect(() => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper) {
      return;
    }

    const handleScroll = () => {
      setIsScrolledHorizontally(wrapper.scrollLeft > 0);
    };

    wrapper.addEventListener('scroll', handleScroll);
    return () => {
      wrapper.removeEventListener('scroll', handleScroll);
    };
  }, [tableWrapperRef]);

  const tableContent = (
    <TableWrapper>
      {!error && (
        <StyledTable data-cy={dataCy} style={style}>
          {headers && (
            <thead>
              <tr style={{ height: compact ? 50 : 'auto' }}>
                {headers.map((x, idx) => (
                  <Th
                    key={idx}
                    index={idx}
                    compact={compact}
                    fix={fixColumnIndex.includes(idx)}
                    isScrolledHorizontally={isScrolledHorizontally}
                    style={{
                      width:
                        columnWidths.length > idx - 1 ? columnWidths[idx] : '',
                    }}
                  >
                    {x}
                  </Th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {isLoading && (
              <tr>
                <Td
                  index={0}
                  compact={true}
                  colSpan={4}
                  cellHeight={cellHeight}
                >
                  <LoadingIndicator
                    isLoading={true}
                    height={`${loaderHeight}px`}
                  />
                </Td>
              </tr>
            )}
            {!isLoading &&
              rows.map(({ key, columns, onClick }) => (
                <TbodyTr
                  key={key}
                  onClick={onClick}
                  compact={compact}
                  hoverable={hoverable}
                >
                  {columns.map((x, idx) => (
                    <Td
                      key={`${key}${idx}`}
                      index={idx}
                      compact={compact}
                      fix={fixColumnIndex.includes(idx)}
                      isScrolledHorizontally={isScrolledHorizontally}
                      style={{
                        width:
                          columnWidths.length > idx - 1
                            ? columnWidths[idx]
                            : '',
                        position: relative ? 'relative' : undefined,
                      }}
                      cellHeight={cellHeight}
                    >
                      {x}
                    </Td>
                  ))}
                </TbodyTr>
              ))}
          </tbody>
        </StyledTable>
      )}
    </TableWrapper>
  );

  // Conditionally wrap the table in a horizontaly scrollable container
  return horizontalScroll ? (
    <div style={{ overflowX: 'auto' }} ref={tableWrapperRef}>
      {tableContent}
    </div>
  ) : (
    tableContent
  );
};
